import { useEventListener } from '@vueuse/core'
import { nextTick, onScopeDispose, type Ref } from 'vue'
import { useRouter } from 'vue-router'

// Dashboard pages scroll inside the shell's page card, not the window, so the
// router's scrollBehavior never sees them. Positions are keyed by the history
// entry index vue-router keeps in history.state.position.
function historyPosition(): number | null {
  const state: unknown = window.history.state
  if (typeof state === 'object' && state !== null && 'position' in state && typeof state.position === 'number') {
    return state.position
  }
  return null
}

// Pages render skeletons and async sections first, so a scroll applied once
// lands on different content after the layout settles. Re-apply it while the
// content resizes, until the user scrolls or the page has had time to load.
const SETTLE_MS = 2000
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const

/** Runs `apply` whenever `content` resizes, until the user acts on `input` or the page settles. Returns the release. */
export function holdWhileSettling(content: Element, input: EventTarget, apply: () => void): () => void {
  const observer = new ResizeObserver(apply)
  observer.observe(content)
  const release = () => {
    observer.disconnect()
    clearTimeout(timer)
    for (const type of USER_SCROLL_EVENTS) {
      input.removeEventListener(type, release)
    }
  }
  const timer = setTimeout(release, SETTLE_MS)
  for (const type of USER_SCROLL_EVENTS) {
    input.addEventListener(type, release, { passive: true })
  }
  return release
}

export function usePageScrollRestoration(scroller: Readonly<Ref<HTMLElement | null>>) {
  const router = useRouter()
  const positions = new Map<number, number>()
  let isHistoryTraversal = false
  let releaseHold: (() => void) | null = null

  const holdPosition = (element: HTMLElement, top: number) => {
    releaseHold?.()
    releaseHold = null
    element.scrollTop = top
    const content = element.firstElementChild
    if (!content || top === 0) {
      return
    }
    releaseHold = holdWhileSettling(content, element, () => {
      element.scrollTop = top
    })
  }

  useEventListener(
    scroller,
    'scroll',
    () => {
      const key = historyPosition()
      if (key !== null && scroller.value) {
        positions.set(key, scroller.value.scrollTop)
      }
    },
    { passive: true }
  )
  // Router history listeners fire only for back/forward. A window popstate
  // listener would run too late: the router's own popstate handler can finish
  // the whole navigation in microtasks before it fires.
  const removeHistoryListener = router.options.history.listen(() => {
    isHistoryTraversal = true
  })

  const removeHook = router.afterEach((to, from) => {
    const restore = isHistoryTraversal
    isHistoryTraversal = false
    if (to.path === from.path) {
      return
    }

    void nextTick(() => {
      const element = scroller.value
      const key = historyPosition()
      if (!element) {
        return
      }
      if (restore && key !== null) {
        holdPosition(element, positions.get(key) ?? 0)
        return
      }
      holdPosition(element, 0)
      if (key !== null) {
        positions.delete(key)
      }
    })
  })
  onScopeDispose(() => {
    releaseHold?.()
    removeHook()
    removeHistoryListener()
  })
}
